import {
  DEFAULT_BRAND,
  DEFAULT_PHONE_NUMBER,
} from '@thesavingsgroup/constants';
import lodashGet from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { StateCreator } from 'zustand';

import { toBrandDisclosure } from './functions/toBrandDisclosure';
import { LeadChannelState } from './types/LeadChannelState';
import { LeadChannelStateDependencies } from './types/LeadChannelStateDependencies';

// we do not persist lastFetchedLeadChannelCode: persisting it would make loadLeadChannel
// skip the fetch on return visits, so a partner's logo or color change would never reach a returning user
export const keysToPersist = ['leadChannel'];

export const leadChannelStore =
  ({
    useApiStore,
    useDashboardStore,
    useParameterStore,
  }: LeadChannelStateDependencies): StateCreator<LeadChannelState> =>
  (set, get) => {
    let inFlightRequest: { code: string; promise: Promise<void> } | null = null;

    const isCurrentRequest = (code: string): boolean =>
      inFlightRequest?.code === code;

    return {
      initLeadChannelFinished: false,
      lastFetchedLeadChannelCode: null,
      leadChannel: {},

      computed: {
        get brand(): string {
          const leadChannel = get().leadChannel;

          return lodashGet(leadChannel, 'brand', DEFAULT_BRAND);
        },
        get customLanderImageURL(): string {
          const leadChannel = get().leadChannel;

          return lodashGet(leadChannel, 'customLanderImage.url', '');
        },
        get defaultLeadChannelCode(): string {
          return 'CONSUMER_PORTAL';
        },
        get disclosureBrand(): string {
          const leadChannel = get().leadChannel;

          return toBrandDisclosure(lodashGet(leadChannel, 'brand', '')).name;
        },
        get displayLabel(): string {
          const leadChannel = get().leadChannel;

          return lodashGet(leadChannel, 'displayLabel', '');
        },
        get isValidLeadChannel(): boolean {
          return !isEmpty(get().leadChannel);
        },
        get phoneNumber(): string {
          const leadChannel = get().leadChannel;

          const phoneNumber = lodashGet(leadChannel, 'phoneNumber', '');

          return isEmpty(phoneNumber) ? DEFAULT_PHONE_NUMBER : phoneNumber;
        },
        get phoneNumberWithCountryCode(): string {
          const phoneNumber = get().computed.phoneNumber;

          return phoneNumber.startsWith('1-')
            ? phoneNumber
            : `1-${phoneNumber}`;
        },
        get primaryLogoURL(): string {
          const leadChannel = get().leadChannel;

          return lodashGet(leadChannel, 'primaryLogo.url', '');
        },
        get privacyPolicyURL(): string {
          const leadChannel = get().leadChannel;

          return toBrandDisclosure(lodashGet(leadChannel, 'brand', ''))
            .privacyPolicyURL;
        },
        get termsOfUseURL(): string {
          const leadChannel = get().leadChannel;

          return toBrandDisclosure(lodashGet(leadChannel, 'brand', ''))
            .termsOfUseURL;
        },
      },

      /**
       * Adopts the brand of whatever vehicle the user has open. Started once at
       * bootstrap, alongside themeStore.publishCssVariables.
       */
      bindCurrentVehicleListener: (): void => {
        useDashboardStore.subscribe((): void => {
          const code =
            useDashboardStore.getState().computed
              .vehicleApplicationLeadChannelCode;

          if (!code) {
            return;
          }

          get().loadLeadChannel(code);
        });
      },

      /**
       * Joins the request already in flight so a route loader can hold the
       * render until branding is settled, rather than painting the previous
       * channel's branding and then swapping. Resolves immediately when
       * nothing is pending.
       */
      ensureLeadChannelLoaded: (): Promise<void> => {
        return inFlightRequest?.promise ?? Promise.resolve();
      },

      // resolves branding from the URL once per page load - a no-op after that,
      // so an opened vehicle's channel wins from then on
      initLeadChannel: (): Promise<void> | undefined => {
        if (get().initLeadChannelFinished) {
          return;
        }

        return get().loadLeadChannel(
          useParameterStore.getState().leadChannelCode,
        );
      },

      loadLeadChannel: (
        leadChannelCode: string | null | undefined,
      ): Promise<void> => {
        const { lastFetchedLeadChannelCode } = get();

        // already resolved this code: skip re-fetching it
        if (
          !leadChannelCode ||
          lastFetchedLeadChannelCode === leadChannelCode
        ) {
          set({ initLeadChannelFinished: true });
          return;
        }

        if (inFlightRequest?.code === leadChannelCode) {
          return inFlightRequest.promise;
        }

        const portalHttpRequest = useApiStore.getState().portalApi();

        const promise = portalHttpRequest
          .get(`/ui-configuration/lead-channel/${leadChannelCode}`)
          .then((leadChannel): void => {
            // a newer code replaced this one while it was in flight, discard the stale result
            if (!isCurrentRequest(leadChannelCode)) {
              return;
            }

            // recorded only on success, so a failed fetch stays retryable
            set({
              lastFetchedLeadChannelCode: leadChannelCode,
              leadChannel,
            });
          })
          .catch((error: unknown): void => {
            console.error(error);
          })
          .finally((): void => {
            // only the current request may clear the slot, a replaced one landing late must not
            if (isCurrentRequest(leadChannelCode)) {
              inFlightRequest = null;
            }

            set({ initLeadChannelFinished: true });
          });

        inFlightRequest = { code: leadChannelCode, promise };

        return promise;
      },
    };
  };
